import { Box, Container, Grid, makeStyles, Theme, Typography } from '@material-ui/core';
import React, { FC, Fragment } from 'react';
import SocialFeedWidgetComponent from 'widgets/SocialFeedWidgetComponent';
import { Post } from 'services/blog/types';
import { hex2rgba } from 'utils/colorHelpers';
import { quotes } from 'images/blog';

interface PostSingleProps {
  post: Post;
}

const useStyles = makeStyles((theme: Theme) => ({
  content: {
    marginTop: 0,
    boxShadow: 'none',
    background: 'transparent',
    borderRadius: 20,
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flexStart',
    paddingTop: 50,
    [theme.breakpoints.up('sm')]: {
      padding: 60,
      marginTop: '-70vh',
      background: theme.palette.switchedColors.whiteGray,
      boxShadow: '0px 4px 20px rgb(0 0 0 / 15%)',
    },
  },
  image: {
    width: '100%',
    borderRadius: '20px',
    marginBottom: theme.spacing(6),
  },
  text: {
    marginBottom: theme.spacing(4),
    textAlign: 'left',

    [theme.breakpoints.up('md')]: {
      textAlign: 'justify',
    },
    [theme.breakpoints.up('sm')]: {
      marginBottom: theme.spacing(5),
    },
  },
  ariaItem: {
    listStyle: 'disc',
    [theme.breakpoints.up('md')]: {
      textAlign: 'justify',
      paddingLeft: theme.spacing(4),
    },
  },
  item: {
    marginBottom: theme.spacing(3),
  },
  containerUser: {
    marginTop: theme.spacing(7),
    alignItems: 'center',
  },
  socialLinks: {
    alignItems: 'center',
  },
  userPhoto: {
    maxWidth: 77,
    maxHeight: 77,
    borderRadius: '50%',
    marginRight: theme.spacing(2),
  },
  line: {
    backgroundColor: theme.palette.switchedColors.mutedBg,
    width: '100%',
    height: 2,
  },
  alignLinks: {
    display: 'flex',
    alignItems: 'center',
  },
  tag: {
    width: 'fit-content',
    borderRadius: theme.spacing(1),
    padding: theme.spacing(1, 4),
    marginBottom: theme.spacing(5),
    marginRight: theme.spacing(4),
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(1, 6),
      marginBottom: theme.spacing(6),
    },
  },
  boxTag: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  sectionText: {
    '& ul': {
      listStylePosition: 'inside',
      padding: 0,
    },
    '& blockquote': {
      position: 'relative',
      border: '1px dashed #338877',
      borderRadius: 15,
      margin: '0 20px 0 17px',
      padding: theme.spacing(2, 5, 4),
      '&:before': {
        content: '""',
        top: -17,
        left: -17,
        width: 42,
        height: 38,
        position: 'absolute',
        backgroundColor: '#fff',
        backgroundRepeat: 'no-repeat',
        backgroundImage: `url('${quotes}')`,
      },
      '&:after': {
        content: '""',
        bottom: -22,
        right: -15,
        width: 42,
        height: 38,
        position: 'absolute',
        backgroundColor: '#fff',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'right',
        backgroundImage: `url('${quotes}')`,
      },
    },
  },
}));

const PostSingle: FC<PostSingleProps> = (props) => {
  const { post } = props;
  const classes = useStyles();

  return (
    <Container fixed>
      <Grid container>
        <Grid item xs={12} sm={12} className={classes.content}>
          <Box className={classes.boxTag}>
            {post.tags.map((tag) => (
              <Box
                className={classes.tag}
                key={tag.id}
                style={{
                  backgroundColor: `${hex2rgba(tag.color, 0.2)}`,
                  color: tag.color,
                }}
              >
                <Typography variant="body2">{tag.title}</Typography>
              </Box>
            ))}
          </Box>
          <Typography variant="h2">
            <span>{post.title}</span>
          </Typography>
          <Box mb={10} />
          <img src={post.image} className={classes.image} alt="Post Image" loading="lazy" />

          {post.sections.map((section) => (
            <Fragment key={section.id}>
              <Box mb={8} />
              {section.title && (
                <Typography variant="h3">
                  <Box mb={4} />
                  <span>{section.title}</span>
                </Typography>
              )}
              {section.image && <img src={section.image} className={classes.image} alt="" loading="lazy" />}
              {section.text && (
                <div key={section.id} className={classes.sectionText} dangerouslySetInnerHTML={{ __html: section.text }} />
              )}
            </Fragment>
          ))}

          <Box mb={5} />
          <Box className={classes.line} />
          <Grid container justifyContent="flex-start" className={classes.containerUser}>
            <Grid item>
              <img src={post.author.avatar} alt={'author'} className={classes.userPhoto} loading="lazy" />
            </Grid>
            <Grid item>
              <Typography variant="h4">
                {post.author.first_name} {post.author.last_name}
              </Typography>
              <Typography variant="body2">{post.author.title}</Typography>
            </Grid>
          </Grid>
          <Box mb={1} />
          <Typography className={classes.text} variant={'body2'}>
            {post.author.description}
          </Typography>

          <Box className={classes.line} />
          <Box mb={6} />
          <Grid container justifyContent="flex-start" className={classes.socialLinks}>
            <Grid item>
              <Typography variant="h4">Share on:</Typography>
            </Grid>
            <Grid item className={classes.alignLinks}>
              <SocialFeedWidgetComponent />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Container>
  );
};

export default PostSingle;
